import { FilePdfOutlined, FileExcelOutlined, ReloadOutlined } from "@ant-design/icons";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { Alert, App, Button, Table, Tooltip } from "antd";
import type { TableProps } from "antd";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { formatDate, formatDateTime } from "../api/format";
import { exportToPdf } from "../api/pdf";
import { exportToXlsx } from "../api/xlsx";
import type { ExportColumn } from "../api/xlsx";

/**
 * A column, plus the two things an export needs that the screen cannot always
 * derive: what to write when `render` produces markup, and a heading when
 * `title` is not a plain string.
 */
export type ExportableColumn<T> = NonNullable<TableProps<T>["columns"]>[number] & {
  exportValue?: (row: T) => string | number | boolean | null | undefined;
  exportHeader?: string;
};

/**
 * The one place that decides how a list looks. Every migrated grid goes through
 * it, so paging, density, the error banner, the export and the "row is
 * clickable" affordance stay identical across the screens — the DHTMLX version
 * got that consistency from its grid widget, and this replaces it.
 *
 * Sorting and paging happen client-side on purpose: the list endpoints answer
 * with the complete result set and filter server-side, so there is nothing to
 * page through remotely. That is also what makes the export cheap — the rows
 * are already here.
 */
export interface DataTableProps<T> {
  columns: ExportableColumn<T>[] | undefined;
  rows: T[] | undefined;
  loading: boolean;
  error?: unknown;
  onRowClick?: (row: T) => void;
  rowSelection?: TableProps<T>["rowSelection"];
  rowClassName?: TableProps<T>["rowClassName"];
  /**
   * Defaults to "id". Not every list is keyed by one — a calendar week is
   * identified by its date — so those name their own key.
   */
  rowKey?: TableProps<T>["rowKey"];
  /** Drill-down tables inside a drawer are short — paging just gets in the way. */
  paginated?: boolean;
  /**
   * Offers .xlsx and PDF downloads of the rows currently in the table. The
   * *translated* name of the list: it heads the PDF, names the worksheet and,
   * reduced to a slug, the file. Omitted where an export makes no sense — a
   * two-column picker inside a drawer, say.
   */
  exportAs?: string;
}

/**
 * ISO dates and timestamps as the API sends them. A column that renders one
 * through `formatDate` would otherwise export the raw "2026-08-04", which is
 * neither what the screen shows nor the reader's date format.
 */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}(?:$|[T ])/;

function exportedValue(
  value: string | number | boolean | null | undefined,
): string | number | boolean | null | undefined {
  if (typeof value !== "string" || !ISO_DATE.test(value)) return value;
  return value.length > 10 ? formatDateTime(value) : formatDate(value);
}

/**
 * A file name from the list's translated name: no spaces, no diacritics, no
 * punctuation a file system might object to.
 */
function fileNameOf(name: string): string {
  const slug = name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return slug || "export";
}

/**
 * Turns the table columns into sheet columns. A column contributes when it has
 * both a heading and a way to read a value; the action column has neither, so
 * it drops out by itself.
 */
function exportColumnsOf<T>(columns: ExportableColumn<T>[]): ExportColumn<T>[] {
  const result: ExportColumn<T>[] = [];

  for (const column of columns) {
    const header =
      column.exportHeader ??
      ("title" in column && typeof column.title === "string" ? column.title : undefined);
    if (!header) continue;

    const read = column.exportValue;
    if (read) {
      result.push({ header, value: read });
      continue;
    }

    const dataIndex = "dataIndex" in column ? column.dataIndex : undefined;
    if (typeof dataIndex !== "string") continue;

    result.push({
      header,
      value: (row) =>
        exportedValue(
          (row as Record<string, unknown>)[dataIndex] as string | number | boolean,
        ),
    });
  }

  return result;
}

export function DataTable<T extends object>({
  columns,
  rows,
  loading,
  error,
  onRowClick,
  rowSelection,
  rowClassName,
  rowKey = "id" as TableProps<T>["rowKey"],
  paginated = true,
  exportAs,
}: DataTableProps<T>) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const fetching = useIsFetching();
  const [exporting, setExporting] = useState<"xlsx" | "pdf" | null>(null);

  // The screens hold their rows in react-query, and a table is not told which
  // keys they live under. Refetching the *active* queries is the same set: what
  // is mounted right now is this screen's data (and the sidebar, whose badge
  // counts are worth refreshing along with it).
  const reload = useCallback(() => {
    void queryClient.refetchQueries({ type: "active" });
  }, [queryClient]);

  // Both formats read the same column contract, so they only differ in the
  // writer they hand it to.
  const exportRows = useCallback(
    async (format: "xlsx" | "pdf") => {
      if (!columns || !rows?.length) return;

      setExporting(format);
      try {
        const name = exportAs ?? "";
        const shared = {
          rows,
          columns: exportColumnsOf(columns),
          fileName: fileNameOf(name),
          sheetName: name,
          yes: t("yes"),
        };

        if (format === "xlsx") {
          await exportToXlsx(shared);
        } else {
          await exportToPdf({ ...shared, title: name });
        }
      } catch (failure) {
        void message.error(failure instanceof Error ? failure.message : String(failure));
      } finally {
        setExporting(null);
      }
    },
    [columns, rows, exportAs, t, message],
  );

  return (
    <>
      {error != null && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          title={error instanceof Error ? error.message : String(error)}
        />
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 8 }}>
        <Tooltip title={t("app.reload")}>
          <Button
            size="small"
            icon={<ReloadOutlined />}
            loading={fetching > 0}
            onClick={reload}
          >
            {t("app.reload")}
          </Button>
        </Tooltip>

        {exportAs && (
          <>
            <Tooltip title={t("app.export_xlsx")}>
              <Button
                size="small"
                icon={<FileExcelOutlined />}
                loading={exporting === "xlsx"}
                // Nothing loaded yet, or nothing to write.
                disabled={loading || !rows?.length || exporting !== null}
                onClick={() => void exportRows("xlsx")}
              >
                {t("app.export_xlsx")}
              </Button>
            </Tooltip>
            <Tooltip title={t("app.export_pdf")}>
              <Button
                size="small"
                icon={<FilePdfOutlined />}
                loading={exporting === "pdf"}
                disabled={loading || !rows?.length || exporting !== null}
                onClick={() => void exportRows("pdf")}
              >
                {t("app.export_pdf")}
              </Button>
            </Tooltip>
          </>
        )}
      </div>

      <Table<T>
        rowKey={rowKey}
        size="small"
        columns={columns}
        dataSource={rows ?? []}
        loading={loading}
        rowSelection={rowSelection}
        rowClassName={rowClassName}
        // From our own bundle rather than antd's locale: this is the one string
        // a user reads on every screen that has nothing to show, and it must not
        // depend on antd resolving its locale the way we expect.
        locale={{ emptyText: t("app.no_data") }}
        onRow={
          onRowClick
            ? (record) => ({
                onClick: (event) => {
                  // Ein Klick auf eine Schaltflaeche, einen Link oder ein
                  // Eingabefeld in der Zeile gehoert diesem Element und nicht
                  // der Zeile. Sonst oeffnet der Bearbeiten-Knopf zusaetzlich
                  // die Schublade, die sich dann ueber den Dialog legt —
                  // gemeldet am 27.09.2026.
                  const target = event.target as HTMLElement;
                  if (target.closest("button, a, input, label, .ant-select, .ant-picker")) return;

                  onRowClick(record);
                },
                style: { cursor: "pointer" },
              })
            : undefined
        }
        pagination={
          paginated
            ? {
                defaultPageSize: 25,
                showSizeChanger: true,
                pageSizeOptions: [10, 25, 50, 100],
                showTotal: (total) => t("app.records", { total }),
              }
            : false
        }
        scroll={{ x: "max-content" }}
      />
    </>
  );
}
